import { Controller } from "@hotwired/stimulus";
import { t } from "../../utils/i18n";
import { dsConfirm } from "../../utils/ds_confirm";

export default class extends Controller {
  static targets = [
    "modal",
    "form",
    "list",
    "sidebar",
    "fileList",
    "fileListEdit",
    "fileInput",
    "documentId",
    "documentName",
  ];

  connect() {
    // Initialisation du contrôleur
    console.log("Document controller connected");

    // Récupérer la locale depuis l'URL
    this.locale = this.getLocaleFromPath();
  }

  getLocaleFromPath() {
    const pathSegments = window.location.pathname.split("/").filter(Boolean);
    const locale = pathSegments[0];
    return ["en", "fr"].includes(locale) ? locale : "fr";
  }

  buildPath(path) {
    return `/${this.locale}${path}`;
  }

  openModal() {
    this.modalTarget.hidden = false;
    // Réinitialiser le formulaire
    this.formTarget.reset();

    // Décocher toutes les checkboxes de visibilité
    const visibilityCheckboxes = document.querySelectorAll(
      'input[name="school_document[visibility][]"]',
    );
    visibilityCheckboxes.forEach((checkbox) => {
      checkbox.checked = false;
    });

    // Cacher l'ID du document (pour la création)
    if (this.hasDocumentIdTarget) {
      this.documentIdTarget.value = "";
    }

    // Réinitialiser le titre du modal
    const modalTitle = document.getElementById("modal-title");
    if (modalTitle) {
      modalTitle.textContent = t("js.config.documents.modal.title_new");
    }

    // Réinitialiser l'action du formulaire
    const form = document.getElementById("school-document-form");
    if (form) {
      form.action = this.buildPath("/schools/settings/documents");
      // Supprimer le champ _method s'il existe
      const methodInput = form.querySelector('input[name="_method"]');
      if (methodInput) {
        methodInput.remove();
      }
    }

    // Vider la liste des fichiers existants
    if (this.hasFileListEditTarget) {
      this.fileListEditTarget.innerHTML = "";
    }
  }

  closeModal() {
    this.modalTarget.hidden = true;
  }

  openSidebar(event) {
    event.preventDefault();
    const documentId = event.currentTarget.dataset.schoolDocumentsManagerId;

    // Afficher la sidebar
    this.sidebarTarget.hidden = false;

    // Charger les détails du document
    fetch(this.buildPath(`/schools/settings/documents/${documentId}`), {
      headers: {
        Accept: "text/html",
        "X-Requested-With": "XMLHttpRequest",
      },
    })
      .then((response) => response.text())
      .then((html) => {
        this.fileListTarget.innerHTML = html;
        if (this.hasDocumentIdTarget) {
          this.documentIdTarget.value = documentId;
        }
      })
      .catch((error) =>
        console.error("Erreur lors du chargement du document:", error),
      );
  }

  closeSidebar() {
    this.sidebarTarget.hidden = true;
  }

  editDocument(event) {
    event.preventDefault();
    const documentId = event.currentTarget.dataset.schoolDocumentsManagerId;

    // Charger les détails du document
    fetch(this.buildPath(`/schools/settings/documents/${documentId}/edit`), {
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        // Remplir le formulaire avec les détails du document
        document.getElementById("school_document_name").value = data.name;

        // Pré-cocher les checkboxes de visibilité
        const visibilityCheckboxes = document.querySelectorAll(
          'input[name="school_document[visibility][]"]',
        );
        visibilityCheckboxes.forEach((checkbox) => {
          checkbox.checked =
            data.visibility && data.visibility.includes(checkbox.value);
        });

        this.fileListEditTarget.innerHTML = "";

        // Créer un titre pour la liste des fichiers existants
        const filesTitle = document.createElement("h3");
        filesTitle.className = "text-sm font-medium text-gray-700 mb-2";
        filesTitle.textContent = t("js.config.documents.files.existing_title");

        // Ajouter le titre seulement s'il y a des fichiers
        if (data.files && data.files.length > 0) {
          this.fileListEditTarget.appendChild(filesTitle);
        } else {
          const noFilesMessage = document.createElement("p");
          noFilesMessage.className = "text-sm text-gray-500 italic mb-4";
          noFilesMessage.textContent = t("js.config.documents.files.none");
          this.fileListEditTarget.appendChild(noFilesMessage);
        }

        data.files.forEach((file) => {
          const fileItem = document.createElement("div");
          fileItem.className =
            "flex items-center justify-between py-2 border-b border-gray-200";
          fileItem.innerHTML = `
            <div class="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" class="flex-shrink-0 h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              <span class="ml-2 flex-1 truncate">
                ${file.filename}
              </span>
            </div>
            <div class="ml-4 flex-shrink-0 flex gap-2">
              <a href="#" data-action="school-documents-manager#removeFile" data-school-documents-manager-id="${documentId}" data-file-id="${file.id}" class="text-red-600 hover:text-red-800 text-sm">
                ${t("js.config.documents.files.delete_link") || t("common.actions.delete")}
              </a>
            </div>
          `;
          this.fileListEditTarget.appendChild(fileItem);
        });

        const form = document.getElementById("school-document-form");
        form.action = this.buildPath(
          `/schools/settings/documents/${documentId}`,
        );
        form.insertAdjacentHTML(
          "beforeend",
          '<input type="hidden" name="_method" value="patch">',
        );

        // Changer le titre du modal
        document.getElementById("modal-title").textContent = t(
          "js.config.documents.modal.title_edit",
        );

        // Ouvrir le modal
        this.modalTarget.hidden = false;
      })
      .catch((error) =>
        console.error("Erreur lors du chargement du document:", error),
      );
  }

  async removeFile(event) {
    event.preventDefault();

    const documentId = event.currentTarget.dataset.schoolDocumentsManagerId;
    const fileId = event.currentTarget.dataset.fileId;
    const fileElement = event.currentTarget.closest(
      ".flex.items-center.justify-between",
    );

    if (!(await dsConfirm(t("js.config.documents.files.delete_confirm"), { tone: "danger" }))) {
      return;
    }
    const csrfToken = document.querySelector("meta[name='csrf-token']").content;

    fetch(
      this.buildPath(
        `/schools/settings/documents/${documentId}/files/${fileId}`,
      ),
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          Accept: "application/json",
        },
        credentials: "same-origin",
      },
    )
      .then((response) => {
        if (response.ok) {
          // Supprimer l'élément du DOM si la requête a réussi
          if (fileElement) {
            fileElement.remove();
          }

          // Vérifier s'il reste des fichiers, sinon afficher un message
          if (this.fileListTarget.children.length === 0) {
            this.fileListTarget.innerHTML = `<p class='text-gray-500 text-sm'>${t("js.config.documents.files.list_empty")}</p>`;
          }
        } else {
          throw new Error("Error removing file");
        }
      })
      .catch((error) => {
        console.error("Error removing file:", error);
        alert(t("js.config.documents.files.delete_error"));
      });
  }

  validateFiles() {
    if (!this.hasFileInputTarget) return true;

    const files = this.fileInputTarget.files;
    let valid = true;

    // Vérifier que tous les fichiers sont des PDF
    for (let i = 0; i < files.length; i++) {
      if (files[i].type !== "application/pdf") {
        alert(
          t("js.config.documents.validations.pdf_only", {
            filename: files[i].name,
          }),
        );
        valid = false;
        break;
      }

      // Vérifier la taille du fichier (max 10MB)
      if (files[i].size > 10 * 1024 * 1024) {
        alert(
          t("js.config.documents.validations.size_limit", {
            filename: files[i].name,
          }),
        );
        valid = false;
        break;
      }
    }

    return valid;
  }

  submitForm(event) {
    if (!this.validateFiles()) {
      event.preventDefault();
    }
  }
}
